import { Component, signal, computed, effect, inject } from "@angular/core";

import { FormsModule } from "@angular/forms";
import { SearchBoxComponent } from "../../../shared/components/search-box/search-box.component";
import { SpinnerLoaderComponent } from "../../../shared/components/spinner-loader/spinner-loader.component";
import { ConfirmService } from "../../../Utils/_services/confirmation-dialog.service";
import { VerificationService } from "../../../Utils/_services/admin/verification/verification.service";
import { Subject, debounceTime, distinctUntilChanged, finalize, forkJoin } from "rxjs";
import { VerificationRequest } from "../../../Utils/_models/verification-request.modal";
import { TooltipModule } from "primeng/tooltip";

@Component({
    selector: "app-verification-requests",
    standalone: true,
    imports: [FormsModule, SearchBoxComponent, SpinnerLoaderComponent, TooltipModule],
    templateUrl: "./verification-requests.component.html",
    styleUrl: "./verification-requests.component.css",
})
export class VerificationRequestsComponent {
    private verificationService = inject(VerificationService);
    private confirmationService = inject(ConfirmService);

    private allRequests = signal<VerificationRequest[]>([]);
    private backendTotalCount = signal<number>(0);
    loading = signal(false);

    private searchSubject = new Subject<string | null>();

    searchTerm = signal<string | null>(null);
    filterStatus = signal<"all" | "pending" | "verified" | "rejected" | "expired" | "failed">("all");
    allSelected = signal(false);
    hasSelection = computed(() => this.allRequests().some(r => r.selected));

    pageSizes = [5, 10, 20, 50];
    pageSize = signal(5);
    currentPage = signal(1);

    currentSortField = signal<string | null>(null);
    currentSortOrder = signal<'asc' | 'desc' | null>(null);
    originalRequests = signal<VerificationRequest[]>([]);

    isModalOpen = false;
    selectedRequest: VerificationRequest | null = null;

    constructor() {
        this.searchSubject.pipe(
            debounceTime(400),
            distinctUntilChanged()
        ).subscribe(term => {
            this.searchTerm.set(term);
            this.currentPage.set(1);
        });

        effect(() => {
            this.loadRequests();
        });
    }

    loadRequests(): void {
        const params = {
            status: this.filterStatus(),
            search: this.searchTerm() || '',
            page: this.currentPage(),
            limit: this.pageSize()
        };

        this.loading.set(true);
        this.verificationService.getAll(params)
            .pipe(finalize(() => this.loading.set(false)))
            .subscribe({
                next: (res: any) => {
                    if (res && res.data) {
                        const list = (res.data.results || []).map((r: any) => ({
                            ...r,
                            selected: false,
                            id: r.session_id,
                            name: r.full_name,
                            email: r.email_address,
                            avatarUrl: r.portrait_image,
                            submittedAt: r.created_at,
                            type: r.session_kind,
                            hasId: !!r.document_type,
                            hasSelfie: !!r.portrait_image,
                            sourceImage: r.additional_details?.face_match?.source_image,
                            targetImage: r.additional_details?.face_match?.target_image,
                            approvedAt: r.status === 'Approved' ? r.created_at : undefined
                        }));
                        this.allRequests.set(list);
                        this.originalRequests.set([...list]);
                        this.backendTotalCount.set(res.data.count || 0);
                        if (this.currentSortField() && this.currentSortOrder()) {
                            this.sortRequestsLocal();
                        } else {
                            this.syncSelectAll();
                        }
                    }
                },
                error: (err: any) => {
                    console.error('Failed to load verification requests:', err);
                    this.allRequests.set([]);
                    this.originalRequests.set([]);
                    this.backendTotalCount.set(0);
                }
            });
    }

    openViewModal(request: VerificationRequest): void {
        this.selectedRequest = request;
        this.isModalOpen = true;
    }

    closeViewModal(): void {
        this.isModalOpen = false;
        this.selectedRequest = null;
    }

    totalCount = computed(() => this.backendTotalCount());
    startIndex = computed(() => this.totalCount() === 0 ? 0 : (this.currentPage() - 1) * this.pageSize() + 1);
    endIndex = computed(() => Math.min(this.currentPage() * this.pageSize(), this.totalCount()));

    onPageSizeChange(newSize: any): void {
        this.pageSize.set(Number(newSize));
        this.currentPage.set(1);
    }

    totalPages = computed(() =>
        Math.ceil(this.totalCount() / this.pageSize())
    );

    paginatedRequests = computed(() => this.allRequests());

    pageNumbers = computed(() =>
        Array.from({ length: this.totalPages() }, (_, i) => i + 1)
    );

    toggleSelectAll(): void {
        const newVal = !this.allSelected();
        this.allSelected.set(newVal);
        this.allRequests.update(list =>
            list.map(r => ({ ...r, selected: newVal }))
        );
    }

    syncSelectAll(): void {
        const list = this.paginatedRequests();
        this.allSelected.set(list.length > 0 && list.every(r => r.selected));
        this.allRequests.update(v => [...v]);
    }

    onSearch(term: string | null) {
        this.searchSubject.next(term);
    }

    onStatusChange(status: any) {
        this.filterStatus.set(status);
        this.currentPage.set(1);
    }

    goToPage(p: number) {
        this.currentPage.set(p);
    }

    nextPage() {
        if (this.currentPage() < this.totalPages()) {
            this.currentPage.update(v => v + 1);
        }
    }

    prevPage() {
        if (this.currentPage() > 1) {
            this.currentPage.update(v => v - 1);
        }
    }

    async approve(id: string) {
        const request = this.allRequests().find(r => r.session_id === id);
        if (!request) return;

        const confirmed = await this.confirmationService.confirm({
            header: 'Approve Verification',
            bodyHeader: 'Are you sure?',
            bodySubtitle: 'You are about to approve this verification request.',
            yesButtonText: 'Approve',
            yesBtnCss: 'btn-primary',
            noBtnCss: 'btn-secondary',
            noButtonText: 'Cancel',
            width: '500px',
        });

        if (confirmed) {
            this.verificationService.updateStatus(id, 'Approve').subscribe({
                next: () => {
                    this.loadRequests();
                    this.closeViewModal();
                },
                error: (err: any) => console.error('Approve failed:', err)
            });
        }
    }

    async reject(id: string) {
        const request = this.allRequests().find(r => r.session_id === id);
        if (!request) return;

        const confirmed = await this.confirmationService.confirm({
            header: 'Reject Verification',
            bodyHeader: 'Are you sure?',
            bodySubtitle: 'You are about to reject this verification request.',
            yesButtonText: 'Reject',
            yesBtnCss: 'btn-danger',
            noBtnCss: 'btn-secondary',
            noButtonText: 'Cancel',
            width: '500px',
        });

        if (confirmed) {
            this.verificationService.updateStatus(id, 'Rejected').subscribe({
                next: () => {
                    this.loadRequests();
                    this.closeViewModal();
                },
                error: (err: any) => console.error('Reject failed:', err)
            });
        }
    }

    async bulkDelete() {
        const selectedRequests = this.allRequests().filter(r => r.selected);
        const selectedCount = selectedRequests.length;
        if (selectedCount === 0) return;

        const confirmed = await this.confirmationService.confirm({
            header: 'Delete Verification Requests',
            bodyHeader: 'Are you sure?',
            bodySubtitle: `You are about to delete ${selectedCount} selected request(s).`,
            yesButtonText: 'Delete All',
            yesBtnCss: 'btn-danger',
            noBtnCss: 'btn-secondary',
            noButtonText: 'Cancel',
            width: '500px',
        });

        if (confirmed) {
            this.loading.set(true);
            const deleteObservables = selectedRequests.map(r => this.verificationService.deleteRequest(r.session_id));
            forkJoin(deleteObservables).pipe(
                finalize(() => this.loading.set(false))
            ).subscribe({
                next: () => {
                    this.allSelected.set(false);
                    this.loadRequests();
                },
                error: (err: any) => console.error('Bulk delete failed:', err)
            });
        }
    }

    openImage(url?: string): void {
        if (url) {
            window.open(url, '_blank');
        }
    }

    onSort(field: string): void {
        if (this.currentSortField() === field) {
            if (this.currentSortOrder() === "asc") {
                this.currentSortOrder.set("desc");
            } else if (this.currentSortOrder() === "desc") {
                this.currentSortOrder.set(null);
                this.currentSortField.set(null);
            }
        } else {
            this.currentSortField.set(field);
            this.currentSortOrder.set("asc");
        }
        this.sortRequestsLocal();
    }

    sortRequestsLocal(): void {
        const field = this.currentSortField();
        const order = this.currentSortOrder();

        if (!field || !order) {
            this.allRequests.set([...this.originalRequests()]);
            this.syncSelectAll();
            return;
        }

        const direction = order === "asc" ? 1 : -1;
        const sorted = [...this.allRequests()];

        sorted.sort((a: any, b: any) => {
            let valA: any = a[field];
            let valB: any = b[field];

            if (field === 'name') {
                valA = (a.name || '').toLowerCase();
                valB = (b.name || '').toLowerCase();
            } else if (field === 'email') {
                valA = (a.email || '').toLowerCase();
                valB = (b.email || '').toLowerCase();
            } else if (field === 'submittedAt') {
                valA = a.submittedAt ? new Date(a.submittedAt).getTime() : 0;
                valB = b.submittedAt ? new Date(b.submittedAt).getTime() : 0;
            } else if (field === 'type') {
                valA = (a.type || '').toLowerCase();
                valB = (b.type || '').toLowerCase();
            } else if (field === 'status') {
                valA = (a.status || '').toLowerCase();
                valB = (b.status || '').toLowerCase();
            }

            if (valA < valB) return -1 * direction;
            if (valA > valB) return 1 * direction;
            return 0;
        });

        this.allRequests.set(sorted);
        this.syncSelectAll();
    }

    getSortIcon(field: string): string {
        if (this.currentSortField() !== field) return "⇅";
        if (this.currentSortOrder() === "asc") return "↑";
        if (this.currentSortOrder() === "desc") return "↓";
        return "⇅";
    }
}